import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../../shared/components/PageHeader';
import CompanyPeriodSelector from '../../shared/components/CompanyPeriodSelector';
import { useLanguage } from '../../app/i18n/LanguageContext';
import { useCompanyWriteAccess } from '../../shared/auth/useCompanyWriteAccess';
import {
  getWorksheetStatus,
  getWorksheetDetail,
  saveWorksheetInputs,
  computeWorksheet,
  computeAllWorksheets,
  toggleWorksheet,
  advanceSession,
  lockSession,
  unlockSession,
  resetSession,
} from '../../services/ifrsWorksheet';
import './IfrsWorksheetPage.css';

// -----------------------------------------------------------------------
// Her standart icin form alani tanimi (config-driven)
// -----------------------------------------------------------------------
const STANDARD_FIELDS = {
  IAS_2: [
    { key: 'nrvAmount', label: 'Net Gerçekleşebilir Değer (NRD) — TL', labelEn: 'Net Realisable Value (NRV) - TRY', type: 'number', placeholder: 'Dönem sonu NRD tutarı', placeholderEn: 'Period-end NRV amount' },
    { key: 'writeDownNotes', label: 'Değer Düşüklüğü Açıklaması (opsiyonel)', labelEn: 'Impairment Notes (optional)', type: 'text', placeholder: 'Ör: Yaşlı stok, bozulma...', placeholderEn: 'Example: aged inventory, obsolescence...' },
  ],
  IAS_16: [
    { key: 'acquisitionCost', label: 'Edinim Maliyeti — TL', labelEn: 'Acquisition Cost - TRY', type: 'number', placeholder: 'Brüt defter değeri', placeholderEn: 'Gross carrying amount' },
    { key: 'residualValue', label: 'Kalıntı Değer — TL', labelEn: 'Residual Value - TRY', type: 'number', placeholder: 'Ekonomik ömür sonu değeri', placeholderEn: 'Value at the end of useful life' },
    { key: 'ifrsUsefulLifeYears', label: 'UFRS Faydalı Ömür (yıl)', labelEn: 'IFRS Useful Life (years)', type: 'number', placeholder: 'Ör: 20', placeholderEn: 'Example: 20' },
    { key: 'vukUsefulLifeYears', label: 'VUK Faydalı Ömür (yıl)', labelEn: 'Local GAAP Useful Life (years)', type: 'number', placeholder: 'Ör: 5', placeholderEn: 'Example: 5' },
    { key: 'monthsInService', label: 'Hizmette Geçen Süre (ay)', labelEn: 'Months in Service', type: 'number', placeholder: 'Ör: 36', placeholderEn: 'Example: 36' },
  ],
  IFRS_9: [
    { key: 'pdPercent', label: 'Temerrüt Olasılığı — PD (0–1)', labelEn: 'Probability of Default - PD (0-1)', type: 'number', placeholder: 'Ör: 0.05 = %5', placeholderEn: 'Example: 0.05 = 5%' },
    { key: 'lgdPercent', label: 'Temerrüt Halinde Kayıp — LGD (0–1)', labelEn: 'Loss Given Default - LGD (0-1)', type: 'number', placeholder: 'Ör: 0.45 = %45', placeholderEn: 'Example: 0.45 = 45%' },
  ],
  IFRS_16: [
    { key: '_leaseInfo', label: null, type: 'lease-list' },
  ],
  IAS_21: [
    { key: 'currency', label: 'Para Birimi', labelEn: 'Currency', type: 'text', placeholder: 'Ör: USD, EUR', placeholderEn: 'Example: USD, EUR' },
    { key: 'historicalRate', label: 'Tarihsel Kur (TL/yabancı para)', labelEn: 'Historical Exchange Rate (TRY / foreign currency)', type: 'number', placeholder: 'İşlem tarihindeki kur', placeholderEn: 'Rate at transaction date' },
    { key: 'closingRate', label: 'Dönem Sonu Kuru (TL/yabancı para)', labelEn: 'Closing Exchange Rate (TRY / foreign currency)', type: 'number', placeholder: 'Bilanço tarihindeki kur', placeholderEn: 'Rate at reporting date' },
    { key: 'monetaryAssets', label: 'Parasal Varlık (yabancı para cinsinden)', labelEn: 'Monetary Assets (foreign currency)', type: 'number', placeholder: 'Nakit, alacaklar...', placeholderEn: 'Cash, receivables...' },
    { key: 'monetaryLiabilities', label: 'Parasal Borç (yabancı para cinsinden)', labelEn: 'Monetary Liabilities (foreign currency)', type: 'number', placeholder: 'Borçlar...', placeholderEn: 'Payables, borrowings...' },
  ],
  IAS_19: [
    { key: 'presentValueObligation', label: 'Yükümlülüğün Bugünkü Değeri — TL', labelEn: 'Present Value of Obligation - TRY', type: 'number', placeholder: 'Aktüeryal hesap sonucu', placeholderEn: 'Actuarial valuation result' },
    { key: 'discountRate', label: 'İskonto Oranı (0–1)', labelEn: 'Discount Rate (0-1)', type: 'number', placeholder: 'Ör: 0.12 = %12', placeholderEn: 'Example: 0.12 = 12%' },
    { key: 'serviceCost', label: 'Cari Hizmet Maliyeti — TL (opsiyonel)', labelEn: 'Current Service Cost - TRY (optional)', type: 'number', placeholder: 'Dönem içi biriken hak', placeholderEn: 'Benefit earned during the period' },
  ],
  IAS_37: [
    { key: '_provisionInfo', label: null, type: 'provision-list' },
  ],
  IAS_12: [
    { key: 'corporateTaxRate', label: 'Kurumlar Vergisi Oranı (0–1)', labelEn: 'Corporate Tax Rate (0-1)', type: 'number', placeholder: 'Ör: 0.25 = %25', placeholderEn: 'Example: 0.25 = 25%' },
  ],
};

const STATUS_CONFIG = {
  VUK_READY:          { label: 'VUK Hazır — IFRS Düzeltme Girişi Bekleniyor', labelEn: 'Local GAAP Ready - Awaiting IFRS Adjustments', color: '#6c757d', bg: '#f8f9fa' },
  ADJUSTMENTS_DRAFT:  { label: 'Düzeltme Girişi Devam Ediyor', labelEn: 'IFRS Adjustments in Progress', color: '#0d6efd', bg: '#e7f1ff' },
  IFRS_READY:         { label: 'IFRS Kilitli — Tablolar Hazır', labelEn: 'IFRS Locked - Financial Statements Ready', color: '#198754', bg: '#d1e7dd' },
};

const CALC_OUTPUT_LABELS = {
  costAmount: { tr: 'Maliyet Bedeli', en: 'Cost' },
  nrvAmount: { tr: 'Net Gerçekleşebilir Değer', en: 'Net Realisable Value' },
  writeDown: { tr: 'Değer Düşüklüğü', en: 'Write-Down' },
  affectedVukPrefix: { tr: 'Etkilenen VUK Kodu', en: 'Affected Local GAAP Prefix' },
  acquisitionCost: { tr: 'Edinim Maliyeti', en: 'Acquisition Cost' },
  residualValue: { tr: 'Kalıntı Değer', en: 'Residual Value' },
  vukUsefulLifeYears: { tr: 'VUK Faydalı Ömür', en: 'Local GAAP Useful Life' },
  ifrsUsefulLifeYears: { tr: 'IFRS Faydalı Ömür', en: 'IFRS Useful Life' },
  monthsInService: { tr: 'Hizmette Geçen Süre', en: 'Months in Service' },
  vukAccumulatedDep: { tr: 'VUK Birikmiş Amortisman', en: 'Local GAAP Accumulated Depreciation' },
  ifrsAccumulatedDep: { tr: 'IFRS Birikmiş Amortisman', en: 'IFRS Accumulated Depreciation' },
  depreciationDifference: { tr: 'Amortisman Farkı', en: 'Depreciation Difference' },
  ifrsCarryingAmount: { tr: 'IFRS Defter Değeri', en: 'IFRS Carrying Amount' },
  grossReceivables: { tr: 'Brüt Alacaklar', en: 'Gross Receivables' },
  existingProvision: { tr: 'Mevcut Karşılık', en: 'Existing Provision' },
  pdPercent: { tr: 'Temerrüt Olasılığı (PD)', en: 'Probability of Default (PD)' },
  lgdPercent: { tr: 'Temerrüt Halinde Kayıp (LGD)', en: 'Loss Given Default (LGD)' },
  ecl: { tr: 'Beklenen Kredi Zararı', en: 'Expected Credit Loss' },
  additionalProvision: { tr: 'Ek Karşılık', en: 'Additional Provision' },
  rouAsset: { tr: 'Kullanım Hakkı Varlığı', en: 'Right-of-Use Asset' },
  totalLeaseLiability: { tr: 'Toplam Kira Borcu', en: 'Total Lease Liability' },
  currentLeaseLiability: { tr: 'Kısa Vadeli Kira Borcu', en: 'Current Lease Liability' },
  nonCurrentLeaseLiability: { tr: 'Uzun Vadeli Kira Borcu', en: 'Non-Current Lease Liability' },
  annualDepreciation: { tr: 'Yıllık Amortisman', en: 'Annual Depreciation' },
  annualInterestExpense: { tr: 'Yıllık Faiz Gideri', en: 'Annual Interest Expense' },
  vukRentExpenseReplaced: { tr: 'Yerine Geçen VUK Kira Gideri', en: 'Local GAAP Rent Expense Replaced' },
  netPnlImpact: { tr: 'Net Kar/Zarar Etkisi', en: 'Net Profit or Loss Effect' },
  currency: { tr: 'Para Birimi', en: 'Currency' },
  historicalRate: { tr: 'Tarihsel Kur', en: 'Historical Exchange Rate' },
  closingRate: { tr: 'Dönem Sonu Kuru', en: 'Closing Exchange Rate' },
  rateDifference: { tr: 'Kur Farkı', en: 'Exchange Rate Difference' },
  monetaryAssets: { tr: 'Parasal Varlıklar', en: 'Monetary Assets' },
  monetaryLiabilities: { tr: 'Parasal Borçlar', en: 'Monetary Liabilities' },
  assetFxDifference: { tr: 'Varlık Kur Farkı', en: 'FX Difference on Assets' },
  liabilityFxDifference: { tr: 'Borç Kur Farkı', en: 'FX Difference on Liabilities' },
  netFxDifference: { tr: 'Net Kur Farkı', en: 'Net FX Difference' },
  presentValueObligation: { tr: 'Yükümlülüğün Bugünkü Değeri', en: 'Present Value of Obligation' },
  vukProvision: { tr: 'VUK Karşılığı', en: 'Local GAAP Provision' },
  additionalLiability: { tr: 'Ek Yükümlülük', en: 'Additional Liability' },
  discountRate: { tr: 'İskonto Oranı', en: 'Discount Rate' },
  totalVukAmount: { tr: 'Toplam VUK Tutarı', en: 'Total Local GAAP Amount' },
  totalIfrsAmount: { tr: 'Toplam IFRS Tutarı', en: 'Total IFRS Amount' },
  netAdjustment: { tr: 'Net Düzeltme', en: 'Net Adjustment' },
  provisionCount: { tr: 'Karşılık Sayısı', en: 'Provision Count' },
  corporateTaxRate: { tr: 'Kurumlar Vergisi Oranı', en: 'Corporate Tax Rate' },
  totalTemporaryDifferences: { tr: 'Toplam Geçici Farklar', en: 'Total Temporary Differences' },
  deferredTaxAsset: { tr: 'Ertelenmiş Vergi Varlığı', en: 'Deferred Tax Asset' },
  deferredTaxLiability: { tr: 'Ertelenmiş Vergi Borcu', en: 'Deferred Tax Liability' },
  netDeferredTax: { tr: 'Net Ertelenmiş Vergi', en: 'Net Deferred Tax' }
};

const fmtAmount = (v, locale = 'tr-TR') => {
  if (v === undefined || v === null) return '—';
  const n = Number(v);
  if (isNaN(n)) return '—';
  return n.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fieldText = (field, isTr, key) => (isTr ? field[key] : (field[`${key}En`] || field[key]));
const calcOutputLabel = (key, isTr) => CALC_OUTPUT_LABELS[key]?.[isTr ? 'tr' : 'en'] || key;

// -----------------------------------------------------------------------
// Ana sayfa bileşeni
// -----------------------------------------------------------------------
export default function IfrsWorksheetPage() {
  const { language } = useLanguage();
  const isTr = !String(language || '').toLowerCase().startsWith('en');
  const L = useCallback((en, tr) => (isTr ? tr : en), [isTr]);

  const [selection, setSelection] = useState({ companyId: '', periodId: '' });
  const canWrite = useCompanyWriteAccess(selection.companyId);

  const [status, setStatus] = useState(null);      // IfrsWorksheetStatusResponse
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Acik olan kart kodu
  const [openCard, setOpenCard] = useState(null);

  // Her kart icin detail cache: { [standardCode]: IfrsWorksheetDetailResponse }
  const [detailCache, setDetailCache] = useState({});

  // Her kart icin form state: { [standardCode]: { [fieldKey]: value } }
  const [formState, setFormState] = useState({});

  // Kira sozlesmeleri (IFRS_16 icin)
  const [leases, setLeases] = useState([{ leaseName: '', annualLeasePayment: '', leaseTermYears: '', incrementalBorrowingRate: '' }]);

  // Karsıliklar (IAS_37 icin)
  const [provisions, setProvisions] = useState([{ provisionName: '', vukAmount: '', ifrsAmount: '', justification: '' }]);

  // -----------------------------------------------------------------------
  // Yükleme
  // -----------------------------------------------------------------------
  const loadStatus = useCallback(async (companyId, periodId) => {
    if (!companyId || !periodId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getWorksheetStatus({ companyId, periodId });
      setStatus(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selection.companyId && selection.periodId) {
      loadStatus(selection.companyId, selection.periodId);
      setOpenCard(null);
      setDetailCache({});
      setFormState({});
    }
  }, [selection.companyId, selection.periodId, loadStatus]);

  // -----------------------------------------------------------------------
  // Kart açılınca detay yükle
  // -----------------------------------------------------------------------
  const handleCardToggle = async (code) => {
    const next = openCard === code ? null : code;
    setOpenCard(next);
    if (!next || detailCache[next]) return;
    try {
      const detail = await getWorksheetDetail({
        companyId: selection.companyId,
        periodId: selection.periodId,
        standardCode: next
      });
      setDetailCache(prev => ({ ...prev, [next]: detail }));
      // Form'u mevcut user_inputs ile önceden doldur
      if (detail.userInputs && Object.keys(detail.userInputs).length > 0) {
        setFormState(prev => ({ ...prev, [next]: detail.userInputs }));
        if (next === 'IFRS_16' && detail.userInputs.leases) setLeases(detail.userInputs.leases);
        if (next === 'IAS_37' && detail.userInputs.provisions) setProvisions(detail.userInputs.provisions);
      }
    } catch (e) {
      // Sessizce geç — kart boş açılır
    }
  };

  // -----------------------------------------------------------------------
  // Form değişikliği
  // -----------------------------------------------------------------------
  const handleFieldChange = (code, key, value) => {
    setFormState(prev => ({
      ...prev,
      [code]: { ...(prev[code] || {}), [key]: value }
    }));
  };

  const buildInputsForCode = useCallback((code) => {
    if (code === 'IFRS_16') return { leases };
    if (code === 'IAS_37') return { provisions };
    return { ...(formState[code] || {}) };
  }, [formState, leases, provisions]);

  // -----------------------------------------------------------------------
  // Kaydet
  // -----------------------------------------------------------------------
  const handleSave = async (code) => {
    setActionLoading(true);
    setError(null);
    try {
      const inputs = buildInputsForCode(code);

      const detail = await saveWorksheetInputs({
        companyId: selection.companyId,
        periodId: selection.periodId,
        standardCode: code,
        inputs
      });
      setDetailCache(prev => ({ ...prev, [code]: detail }));
      setSuccessMsg(L(`${code} data saved.`, `${code} verileri kaydedildi.`));
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (e) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  };

  // -----------------------------------------------------------------------
  // Hesapla (tek kart)
  // -----------------------------------------------------------------------
  const handleCompute = async (code) => {
    setActionLoading(true);
    setError(null);
    try {
      const inputs = buildInputsForCode(code);
      const savedDetail = await saveWorksheetInputs({
        companyId: selection.companyId,
        periodId: selection.periodId,
        standardCode: code,
        inputs
      });
      setDetailCache(prev => ({ ...prev, [code]: savedDetail }));

      const detail = await computeWorksheet({
        companyId: selection.companyId,
        periodId: selection.periodId,
        standardCode: code
      });
      setDetailCache(prev => ({ ...prev, [code]: detail }));
      // Kartı özet listesinde de güncelle
      const fresh = await getWorksheetStatus({ companyId: selection.companyId, periodId: selection.periodId });
      setStatus(fresh);
      setSuccessMsg(L(`${code} computed.`, `${code} hesaplandı.`));
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (e) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  };

  // -----------------------------------------------------------------------
  // Tümünü Hesapla
  // -----------------------------------------------------------------------
  const handleComputeAll = async () => {
    setActionLoading(true);
    setError(null);
    try {
      const dirtyCodes = Object.keys(formState).filter((code) =>
        status?.adjustments?.some((card) => card.standardCode === code && card.enabled)
      );

      for (const code of dirtyCodes) {
        const inputs = buildInputsForCode(code);
        const detail = await saveWorksheetInputs({
          companyId: selection.companyId,
          periodId: selection.periodId,
          standardCode: code,
          inputs
        });
        setDetailCache(prev => ({ ...prev, [code]: detail }));
      }

      const fresh = await computeAllWorksheets({ companyId: selection.companyId, periodId: selection.periodId });
      setStatus(fresh);
      setDetailCache({});  // Tüm cache'i temizle — yeniden yüklenecek
      setSuccessMsg(L('All standards computed.', 'Tüm standartlar hesaplandı.'));
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (e) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  };

  // -----------------------------------------------------------------------
  // Session geçişleri
  // -----------------------------------------------------------------------
  const handleSessionAction = async (action) => {
    setActionLoading(true);
    setError(null);
    try {
      const params = { companyId: selection.companyId, periodId: selection.periodId };
      let fresh;
      if (action === 'advance') fresh = await advanceSession(params);
      else if (action === 'lock') fresh = await lockSession(params);
      else if (action === 'unlock') fresh = await unlockSession(params);
      else if (action === 'reset') {
        if (!window.confirm(L(
          'All IFRS adjustment data will be deleted. Continue?',
          'Tüm IFRS düzeltme verileri silinecek. Devam edilsin mi?'
        ))) return;
        fresh = await resetSession(params);
      }
      setStatus(fresh);
      setDetailCache({});
    } catch (e) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  };

  // -----------------------------------------------------------------------
  // Toggle (aktif/pasif)
  // -----------------------------------------------------------------------
  const handleToggle = async (code, enabled) => {
    try {
      await toggleWorksheet({ companyId: selection.companyId, periodId: selection.periodId, standardCode: code, enabled });
      const fresh = await getWorksheetStatus({ companyId: selection.companyId, periodId: selection.periodId });
      setStatus(fresh);
    } catch (e) {
      setError(e.message);
    }
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  const sessionStatus = status?.status || null;
  const cfg = STATUS_CONFIG[sessionStatus] || {};
  const isDraft = sessionStatus === 'ADJUSTMENTS_DRAFT';
  const isReady = sessionStatus === 'IFRS_READY';
  const isVukReady = sessionStatus === 'VUK_READY';

  return (
    <div className="ifrs-worksheet-page">
      <PageHeader
        title={L('IFRS Compliance Worksheet', 'IFRS Uyum Çalışma Sayfası')}
        description={L(
          'Standard-by-standard IFRS adjustments. Lock to generate IFRS financial statements.',
          'Standart bazlı IFRS düzeltmeleri. Kilitleyin → IFRS Mali Tabloları hazır.'
        )}
      />

      <CompanyPeriodSelector onSelectionChange={setSelection} />

      {loading && (
        <div className="ws-loading">{L('Loading...', 'Yükleniyor...')}</div>
      )}

      {error && (
        <div className="ws-banner ws-banner--error">{error}</div>
      )}

      {successMsg && (
        <div className="ws-banner ws-banner--success">{successMsg}</div>
      )}

      {status && !loading && (
        <>
          {/* ---- Session durum banner ---- */}
          <div className="ws-session-banner" style={{ background: cfg.bg, borderLeft: `4px solid ${cfg.color}` }}>
            <span className="ws-session-label" style={{ color: cfg.color }}>
              {L(cfg.labelEn || sessionStatus, cfg.label || sessionStatus)}
            </span>
            {status.lockedAt && (
              <span className="ws-session-meta">
                {L('Locked at:', 'Kilitlenme:')} {new Date(status.lockedAt).toLocaleString(isTr ? 'tr-TR' : 'en-US')}
              </span>
            )}

            <div className="ws-session-actions">
              {isVukReady && canWrite && (
                <button className="ws-btn ws-btn--primary" disabled={actionLoading}
                  onClick={() => handleSessionAction('advance')}>
                  {L('Start IFRS Adjustments →', 'IFRS Düzeltmelerine Başla →')}
                </button>
              )}
              {isDraft && canWrite && (
                <>
                  <button className="ws-btn ws-btn--secondary" disabled={actionLoading}
                    onClick={handleComputeAll}>
                    {L('Compute All', 'Tümünü Hesapla')}
                  </button>
                  <button className="ws-btn ws-btn--success" disabled={actionLoading}
                    onClick={() => handleSessionAction('lock')}>
                    {L('Lock & Generate IFRS →', 'Kilitle & IFRS Üret →')}
                  </button>
                  <button className="ws-btn ws-btn--danger-outline" disabled={actionLoading}
                    onClick={() => handleSessionAction('reset')}>
                    {L('Reset', 'Sıfırla')}
                  </button>
                </>
              )}
              {isReady && canWrite && (
                <>
                  <button className="ws-btn ws-btn--secondary" disabled={actionLoading}
                    onClick={() => handleSessionAction('unlock')}>
                    {L('Unlock for Edit', 'Düzenleme İçin Aç')}
                  </button>
                </>
              )}
            </div>
          </div>

          {/* ---- Standart kartları ---- */}
          {(isDraft || isReady) && status.adjustments && (
            <div className="ws-cards">
              {status.adjustments.map((card) => (
                <StandardCard
                  key={card.standardCode}
                  card={card}
                  open={openCard === card.standardCode}
                  detail={detailCache[card.standardCode] || null}
                  formState={formState[card.standardCode] || {}}
                  leases={leases}
                  provisions={provisions}
                  canWrite={canWrite && isDraft}
                  actionLoading={actionLoading}
                  onToggle={handleCardToggle}
                  onFieldChange={handleFieldChange}
                  onLeasesChange={setLeases}
                  onProvisionsChange={setProvisions}
                  onSave={handleSave}
                  onCompute={handleCompute}
                  onToggleEnabled={handleToggle}
                  isTr={isTr}
                  L={L}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------
// Standart kart bileşeni
// -----------------------------------------------------------------------
function StandardCard({
  card, open, detail, formState, leases, provisions,
  canWrite, actionLoading,
  onToggle, onFieldChange, onLeasesChange, onProvisionsChange,
  onSave, onCompute, onToggleEnabled,
  isTr, L
}) {
  const code = card.standardCode;
  const adj = card.adjustmentAmount;
  const adjNum = Number(adj);
  const hasAdj = !isNaN(adjNum) && adjNum !== 0;
  const adjColor = adjNum > 0 ? '#198754' : adjNum < 0 ? '#dc3545' : '#6c757d';
  const locale = isTr ? 'tr-TR' : 'en-US';
  const currencyLabel = isTr ? 'TL' : 'TRY';
  const standardTitle = isTr
    ? (card.standardTitleTr || card.standardTitleEn || code)
    : (card.standardTitleEn || card.standardTitleTr || code);
  const explanation = isTr
    ? detail?.explanationTr
    : (detail?.explanationEn || detail?.explanationTr);

  return (
    <div className={`ws-card ${!card.enabled ? 'ws-card--disabled' : ''} ${card.computed ? 'ws-card--computed' : ''}`}>
      {/* --- Kart başlığı --- */}
      <div className="ws-card-header" onClick={() => onToggle(code)}>
        <div className="ws-card-header-left">
          <span className="ws-standard-badge">{card.displayCode}</span>
          <span className="ws-standard-title">{standardTitle}</span>
          {card.computed && <span className="ws-computed-check">✓</span>}
        </div>
        <div className="ws-card-header-right">
          {hasAdj && (
            <span className="ws-adj-chip" style={{ color: adjColor }}>
              {adjNum > 0 ? '+' : ''}{fmtAmount(adj, locale)} {currencyLabel}
            </span>
          )}
          {card.kgkUrl && (
            <a href={card.kgkUrl} target="_blank" rel="noreferrer"
              className="ws-kgk-link" onClick={e => e.stopPropagation()}>
              KGK ↗
            </a>
          )}
          {canWrite && (
            <label className="ws-toggle-label" onClick={e => e.stopPropagation()}>
              <input type="checkbox" checked={card.enabled}
                onChange={e => onToggleEnabled(code, e.target.checked)} />
              {card.enabled ? L('Active', 'Aktif') : L('Inactive', 'Pasif')}
            </label>
          )}
          <span className="ws-chevron">{open ? '▲' : '▼'}</span>
        </div>
      </div>

      {/* --- Kart gövdesi --- */}
      {open && (
        <div className="ws-card-body">
          {/* VUK bakiyeleri */}
          {detail?.vukBalances && detail.vukBalances.length > 0 && (
            <div className="ws-vuk-balances">
              <div className="ws-section-label">{L('Local GAAP Balances (VUK)', 'VUK Bakiyeleri')}</div>
              <table className="ws-table">
                <thead>
                  <tr>
                    <th>{L('Account Code', 'Hesap Kodu')}</th>
                    <th>{L('Name', 'Ad')}</th>
                    <th style={{ textAlign: 'right' }}>{L('Amount (TRY)', 'Tutar (TL)')}</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.vukBalances.map(b => (
                    <tr key={b.lineCode}>
                      <td>{b.lineCode}</td>
                      <td>{b.lineName}</td>
                      <td style={{ textAlign: 'right' }}>{fmtAmount(b.amount, locale)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Giriş formu */}
          {canWrite && card.enabled && (
            <div className="ws-form">
              <div className="ws-section-label">{L('IFRS Input Data', 'IFRS Giriş Verileri')}</div>
              {code === 'IFRS_16' ? (
                <LeaseListForm leases={leases} onChange={onLeasesChange} L={L} />
              ) : code === 'IAS_37' ? (
                <ProvisionListForm provisions={provisions} onChange={onProvisionsChange} L={L} />
              ) : (
                (STANDARD_FIELDS[code] || []).map(field => (
                  <div key={field.key} className="ws-form-row">
                    <label>{fieldText(field, isTr, 'label')}</label>
                    <input
                      type={field.type}
                      placeholder={fieldText(field, isTr, 'placeholder')}
                      value={formState[field.key] || ''}
                      onChange={e => onFieldChange(code, field.key, e.target.value)}
                      step="any"
                    />
                  </div>
                ))
              )}

              <div className="ws-form-actions">
                <button className="ws-btn ws-btn--secondary" disabled={actionLoading}
                  onClick={() => onSave(code)}>
                  {L('Save', 'Kaydet')}
                </button>
                <button className="ws-btn ws-btn--primary" disabled={actionLoading}
                  onClick={() => onCompute(code)}>
                  {actionLoading ? L('Computing...', 'Hesaplanıyor...') : L('Compute', 'Hesapla')}
                </button>
              </div>
            </div>
          )}

          {/* Hesaplanan açıklama */}
          {explanation && (
            <div className="ws-explanation">
              <div className="ws-section-label">{L('IFRS Explanation', 'IFRS Açıklaması')}</div>
              <p>{explanation}</p>
            </div>
          )}

          {/* Hesaplanan çıktılar özeti */}
          {detail?.calcOutputs && Object.keys(detail.calcOutputs).length > 0 && (
            <div className="ws-calc-outputs">
              <div className="ws-section-label">{L('Calculation Details', 'Hesaplama Detayları')}</div>
              <table className="ws-table ws-table--compact">
                <tbody>
                  {Object.entries(detail.calcOutputs).map(([k, v]) => (
                    <tr key={k}>
                      <td className="ws-output-key">{calcOutputLabel(k, isTr)}</td>
                      <td style={{ textAlign: 'right' }}>{typeof v === 'number' ? fmtAmount(v, locale) : String(v)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!canWrite && !detail?.computed && (
            <div className="ws-info-text">
              {L('Switch to edit mode to enter data for this standard.', 'Bu standart için giriş yapmak üzere düzenleme moduna geçin.')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------
// IFRS 16 — kira listesi formu
// -----------------------------------------------------------------------
function LeaseListForm({ leases, onChange, L }) {
  const update = (i, key, val) => {
    const next = leases.map((l, idx) => idx === i ? { ...l, [key]: val } : l);
    onChange(next);
  };
  const addRow = () => onChange([...leases, { leaseName: '', annualLeasePayment: '', leaseTermYears: '', incrementalBorrowingRate: '' }]);
  const removeRow = (i) => onChange(leases.filter((_, idx) => idx !== i));

  return (
    <div className="ws-list-form">
      {leases.map((l, i) => (
        <div key={i} className="ws-list-row">
          <input placeholder={L('Lease name', 'Kira adı')} value={l.leaseName}
            onChange={e => update(i, 'leaseName', e.target.value)} />
          <input type="number" placeholder={L('Annual payment (TRY)', 'Yıllık ödeme (TL)')} value={l.annualLeasePayment} step="any"
            onChange={e => update(i, 'annualLeasePayment', e.target.value)} />
          <input type="number" placeholder={L('Lease term (years)', 'Kira süresi (yıl)')} value={l.leaseTermYears} step="1"
            onChange={e => update(i, 'leaseTermYears', e.target.value)} />
          <input type="number" placeholder={L('Incremental borrowing rate (example: 0.25)', 'Artımlı borçlanma oranı (ör: 0.25)')} value={l.incrementalBorrowingRate} step="any"
            onChange={e => update(i, 'incrementalBorrowingRate', e.target.value)} />
          {leases.length > 1 && (
            <button className="ws-btn ws-btn--danger-outline ws-btn--sm" onClick={() => removeRow(i)}>✕</button>
          )}
        </div>
      ))}
      <button className="ws-btn ws-btn--secondary ws-btn--sm" onClick={addRow}>{L('+ Add Lease', '+ Kira Ekle')}</button>
    </div>
  );
}

// -----------------------------------------------------------------------
// IAS 37 — karşılık listesi formu
// -----------------------------------------------------------------------
function ProvisionListForm({ provisions, onChange, L }) {
  const update = (i, key, val) => {
    const next = provisions.map((p, idx) => idx === i ? { ...p, [key]: val } : p);
    onChange(next);
  };
  const addRow = () => onChange([...provisions, { provisionName: '', vukAmount: '', ifrsAmount: '', justification: '' }]);
  const removeRow = (i) => onChange(provisions.filter((_, idx) => idx !== i));

  return (
    <div className="ws-list-form">
      {provisions.map((p, i) => (
        <div key={i} className="ws-list-row">
          <input placeholder={L('Provision name', 'Karşılık adı')} value={p.provisionName}
            onChange={e => update(i, 'provisionName', e.target.value)} />
          <input type="number" placeholder={L('Local GAAP amount (TRY)', 'VUK tutarı (TL)')} value={p.vukAmount} step="any"
            onChange={e => update(i, 'vukAmount', e.target.value)} />
          <input type="number" placeholder={L('IFRS amount (TRY)', 'UFRS tutarı (TL)')} value={p.ifrsAmount} step="any"
            onChange={e => update(i, 'ifrsAmount', e.target.value)} />
          <input placeholder={L('Basis / rationale', 'Dayanak / gerekçe')} value={p.justification}
            onChange={e => update(i, 'justification', e.target.value)} />
          {provisions.length > 1 && (
            <button className="ws-btn ws-btn--danger-outline ws-btn--sm" onClick={() => removeRow(i)}>✕</button>
          )}
        </div>
      ))}
      <button className="ws-btn ws-btn--secondary ws-btn--sm" onClick={addRow}>{L('+ Add Provision', '+ Karşılık Ekle')}</button>
    </div>
  );
}
